<script setup lang="ts">
import { inject, Ref, ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { getCurrentLocale } from '@/i18n';
import capitalize from 'lodash/capitalize';
import cloneDeep from 'lodash/cloneDeep';
import { formatDate } from '@/utils/dateUtils';
import { getTextVariant } from '@/utils/stringUtils';
import { STATUS_CLOSED, STATUS_OPEN } from '@/constants';
import { useGroupTypeStore } from '@/store/GroupTypeStore';
import { useDialog } from 'primevue/usedialog';
import { useTourManagementStore } from '@/store/TourManagementStore';
import { useToast } from 'primevue/usetoast';
import Button from 'primevue/button';
import Dialog from 'primevue/dialog';
import DataTable from 'primevue/datatable';
import InputNumber from 'primevue/inputnumber';
import ToggleSwitch from 'primevue/toggleswitch';
import MultiSelect from 'primevue/multiselect';
import TableColumn from 'primevue/column';
import AutomatedTourCalendar from '@/components/modal/tour/AutomatedTourCalendar.vue';
import LoadingSpinner from '@/components/LoadingSpinner.vue';
import ProgressModal from '../ProgressModal.vue';
import SeeMoreToursDescription from '@/components/modal/tour/SeeMoreToursDescription.vue';

const dialogRef = inject('dialogRef') as Ref;
const automatedTourData = dialogRef?.value?.options?.data?.automatedTourData;
const tourCode = dialogRef?.value?.options?.data?.tourCode;
const onClose = dialogRef?.value?.options?.emits?.onClose;

const automatedTourDataOption = ref(cloneDeep(automatedTourData));
const { t } = useI18n({ useScope: 'global' });
const dialog = useDialog();
const toast = useToast();
const currentLanguage = getCurrentLocale();
const langCapitalize = capitalize(currentLanguage);
const groupTypeManagement = useGroupTypeStore();
const tourManagementStore = useTourManagementStore();
const rowsPerPageOptions = ref([10, 25, 50, 100, 200, 400, 500]);
const checkList = ref([]);
const groupTypeList = ref([]);
const isLoading = ref(false);
const isShowModal = ref(false);
const isLoadingImport = ref(false);
const isSuccess = ref(false);
const fieldNames = automatedTourDataOption.value?.flatMap(Object.keys);
const uniqueFieldNames = [...new Set(fieldNames)];
const maxLengthText = ref(25);
const frozenFields = ref(['checkbox', 'svId', 'ttsId', handleFileName('tourName')]);
const maxRoomDefault = 9;
const listRecord = ref([]);
const dataTableTourDetailRef = ref();

const columns = ref([
  { field: 'checkbox', header: '' },
  { field: 'svId', header: t('management.tour.list_tour_detail.sv_id') },
  { field: 'ttsId', header: t('management.tour.list_tour_detail.tts_id') },
  { field: handleFileName('tourName'), header: t('management.tour.list_tour_detail.tour_name') },
  { field: handleFileName('tourInclusions'), header: t('management.tour.list_tour_detail.tour_inclusions') },
  { field: handleFileName('tourDurations'), header: t('management.tour.list_tour_detail.tour_duration') },
  { field: handleFileName('tourDescription'), header: t('management.tour.list_tour_detail.tours_description') },
  { field: 'destCode', header: t('management.tour.list_tour_detail.destination_code') },
  { field: 'maxRoom', header: t('management.tour.list_tour_detail.max_rooms') },
  { field: 'quoteDate', header: t('management.tour.list_tour_detail.quote_dates') },
  { field: 'travelDate', header: t('management.tour.list_tour_detail.travel_dates') },
  { field: 'blackoutDate', header: t('management.tour.list_tour_detail.blackout_dates') },
  { field: 'groupTypes', header: t('management.tour.list_tour_detail.group_types') },
  { field: 'instaQuote', header: t('management.tour.list_tour_detail.insta_quote') },
  { field: 'status', header: t('management.tour.list_tour_detail.status') }
]);

function handleFileName(fieldName: string) {
  return uniqueFieldNames.includes(fieldName) ? fieldName : `${fieldName}${langCapitalize}`;
}

const isFrozenField = (columnField: string) => {
  return frozenFields.value.includes(columnField);
};

const handleDisplayDate = (listDate: any, separator = '\n') => {
  let format = '';
  if (listDate.length > 0) {
    format = listDate.map((item: any) => `${formatDate(item.start, 'dd-LLL-yyyy', true, currentLanguage)} to ${formatDate(item.end, 'dd-LLL-yyyy', true, currentLanguage)}`).join(separator);
  }
  return format;
};

const getGroupTypeData = async () => {
  groupTypeManagement.getGroupTypeList().then((response) => {
    groupTypeList.value = response.data;
    automatedTourDataOption.value?.forEach((automatedTour: any, index: number) => {
      automatedTour.id = index;
      automatedTour.maxRoom = 9;
      automatedTour.instaQuote = 'OPEN';
      automatedTour.status = 'OPEN';
      automatedTour.groupTypes = groupTypeList.value.map((groupType: any) => groupType?.sk);
    });
  });
};
getGroupTypeData();

const addDates = (dataRow: any, field: string) => {
  const dialogRes = dialog.open(AutomatedTourCalendar, {
    props: {
      header: t('management.tour.automated_tour.select_date'),
      modal: true,
      maximizable: true,
      style: {
        width: '512px'
      }
    },
    emits: {
      onClose: (data: any) => {
        fillDataToRows(dataRow, field, data);
        dialogRes.close();
      }
    }
  });
};

const fillDataToRows = (dataRow: any, field: string, data: any) => {
  dataRow[field] = [data.dates];
  if (data.rowsOnly) {
    automatedTourDataOption.value.forEach((row: any) => {
      if (!row[field] || !row[field].length) {
        row[field] = [data.dates];
      }
    });
  }
  if (data.allRows) {
    automatedTourDataOption.value.forEach((row: any) => {
      row[field] = [data.dates];
    });
  }
};

const handleCloseModal = (value: boolean) => {
  isShowModal.value = value;
  onClose();
};

const updateTour = () => {
  const checkListClone = cloneDeep(checkList.value);
  checkListClone.forEach((data: any) => {
    delete data.id;
  });
  isLoading.value = true;
  isShowModal.value = true;
  isLoadingImport.value = true;
  tourManagementStore
    .addDataAutoSearch(checkListClone)
    .then(() => {
      toast.add({ severity: 'success', summary: '', detail: t('notification.submit_success'), life: 3000 });
      isSuccess.value = true;
    })
    .catch(() => {
      isSuccess.value = false;
    })
    .finally(() => {
      isLoadingImport.value = false;
      isLoading.value = false;
    });
};

const openSeeMore = (dataField: string, header: string) => {
  const dialogRef = dialog.open(SeeMoreToursDescription, {
    props: {
      header: `${header}`,
      modal: true,
      style: {
        width: '65vw'
      }
    },
    data: {
      dataField: dataField
    },
    emits: {
      onClose: () => {
        dialogRef.close();
      }
    }
  });
};

const handleMaxRoomDefault = (data: any, field: string) => {
  if ((!data[field] && data[field] !== 0) || data[field] < 0) {
    data[field] = maxRoomDefault;
  }
};

const listRecordAutoSearch = () => {
  if (tourCode?.length) {
    listRecord.value = automatedTourDataOption.value.filter((data: any) => {
      return tourCode.some((code: string) => data.ttsId.includes(code));
    });
    return listRecord.value;
  } else {
    return automatedTourDataOption.value;
  }
};

// Temporary fix due to issue in PrimeVue 4.2.5 for frozen columns
// https://github.com/primefaces/primevue/issues/6067
// To be removed as soon as we have a fix
const frozenTableAutoTour = () => {
  const tableTours = dataTableTourDetailRef.value?.$el.querySelector('.p-datatable-thead');
  if (tableTours) {
    const colWidths = tableTours.querySelectorAll('th');
    document.documentElement.style.setProperty('--tour-auto-width-1', 0 + 'px');
    document.documentElement.style.setProperty('--tour-auto-width-2', -0.5 + colWidths[0].offsetWidth + 'px');
    document.documentElement.style.setProperty('--tour-auto-width-3', colWidths[0].offsetWidth + colWidths[1].offsetWidth + 'px');
    document.documentElement.style.setProperty('--tour-auto-width-4', colWidths[0].offsetWidth + colWidths[1].offsetWidth + colWidths[2].offsetWidth + 'px');
  }
};

onMounted(() => {
  frozenTableAutoTour();
});
</script>

<template>
  <div class="preview-list-tour">
    <LoadingSpinner v-if="isLoading" />
    <DataTable
      editMode="cell"
      ref="dataTableTourDetailRef"
      :value="listRecordAutoSearch()"
      scrollHeight="600px"
      sortMode="single"
      sortField="type"
      :sortOrder="-1"
      scrollable
      paginator
      :rows="rowsPerPageOptions[0]"
      :rowsPerPageOptions="rowsPerPageOptions"
      v-model:selection="checkList"
      showGridlines
      filterDisplay="menu"
      dataKey="id"
    >
      <template #empty>{{ $t('management.tour.list_tour_detail.fare_not_available') }}</template>
      <TableColumn v-for="column of columns" :key="column.field" :field="column.field" :frozen="isFrozenField(column.field)" :selectionMode="column.field === 'checkbox' ? 'multiple' : undefined">
        <template #header>
          <span :class="{ 'flex justify-content-center gap-3 align-content-center w-full white-space-nowrap': true }">
            {{ column.header }}
          </span>
        </template>
        <template v-if="column.field !== 'checkbox'" #body="{ data, field }">
          <span v-if="field === 'quoteDate' || field === 'blackoutDate' || field === 'travelDate'">
            <span v-if="data[field] && data[field].length">
              {{ handleDisplayDate(data[field]) }}
            </span>
            <span v-else class="underline text-primary cursor-pointer" @click="addDates(data, field)"> {{ $t('management.tour.automated_tour.plus_add') }} </span>
          </span>
          <span v-else-if="field === 'groupTypes'">
            <MultiSelect
              id="group_type"
              v-model="data[field]"
              display="chip"
              :options="groupTypeList"
              :optionLabel="getTextVariant(currentLanguage, 'name', 'nameFr')"
              :maxSelectedLabels="1"
              optionValue="sk"
              :placeholder="$t('advance_quote.form.select_group_type')"
              class="min-w-18rem"
            >
              <template #option="slotProps">
                <div class="max-w-15rem overflow-hidden text-overflow-ellipsis" v-tooltip.left="getTextVariant(currentLanguage, slotProps.option['name'], slotProps.option['nameFr'])">
                  {{ getTextVariant(currentLanguage, slotProps.option['name'], slotProps.option['nameFr']) }}
                </div>
              </template>
            </MultiSelect>
          </span>
          <span v-else-if="field === 'instaQuote' || field === 'status'">
            <ToggleSwitch v-model="data[field]" :trueValue="STATUS_OPEN" :falseValue="STATUS_CLOSED" />
          </span>

          <span v-else-if="field === 'destCode'">
            {{ data[field] }}
          </span>

          <span v-else-if="field === 'maxRoom'">
            <InputNumber :min="0" v-model="data[field]" @update:modelValue="handleMaxRoomDefault(data, field)" />
          </span>

          <div v-else-if="field === handleFileName('tourInclusions') || field === handleFileName('tourDescription')">
            <div class="white-space-nowrap overflow-hidden text-overflow-ellipsis w-20rem">{{ data[field] }}</div>
            <span v-if="data[field] && data[field].length > maxLengthText" class="view-more">
              <a class="text-blue-600 font-bold underline cursor-pointer text-left uppercase" @click="openSeeMore(data[field], column.header)">{{ $t('common.labels.see_more') }}</a>
            </span>
          </div>
          <span v-else>
            {{ data[field] }}
          </span>
        </template>
      </TableColumn>
    </DataTable>
    <div class="text-center">
      <Button id="update-tours-table" icon="pi pi-plus" :label="$t('management.tour.automated_tour.update_tours_table').toUpperCase()" :disabled="!checkList.length" @click="updateTour()"></Button>
    </div>
    <Dialog v-model:visible="isShowModal" :header="$t('common.labels.uploading_tours')" modal :closable="false">
      <ProgressModal :isProgress="isLoadingImport" @handleClose="handleCloseModal" :isSuccess="isSuccess" :isRevert="false" :isAddTour="true" />
    </Dialog>
  </div>
</template>

<style scoped lang="scss">
:deep() {
  .p-frozen-column {
    background: #f8f9fa;
  }
}
</style>
